import {
    HttpClient,
    HttpErrorResponse,
    HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RegistroService } from 'src/app/registro.service';

/** Respuesta del listado de permisos (API `GET /permisos`). */
export interface Permiso {
    id: number;
    codigo: string;
    nombre: string;
    descripcion: string | null;
    estado: boolean;
}

/** Respuesta del listado de roles (API `GET /permisos/roles`). */
export interface Rol {
    id: number;
    codigo: string;
    nombre: string;
    descripcion: string | null;
    estado: boolean;
}

/** Elemento de `GET /permisos/usuarios/keycloak` (Keycloak Admin API). */
export interface UsuarioKeycloak {
    id?: string;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    enabled?: boolean;
    emailVerified?: boolean;
    createdTimestamp?: number;
}

export interface KeycloakUsuariosQuery {
    first?: number;
    max?: number;
    search?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AccessControlService {
    private readonly dominiosInstitucionales =
        environment.dominiosInstitucionales;

    private readonly apiUrl = environment.apiSiriUrl;

    constructor(
        private http: HttpClient,
        private registroService: RegistroService,
        private keycloakService: KeycloakService
    ) {}

    getPermisosByAuthUsuario(): Observable<Permiso[]> {
        const url = `${this.apiUrl}permisos/usuario`;
        return this.http
            .get<Permiso[]>(url)
            .pipe(catchError(this.handleErrorResponse));
    }

    private handleErrorResponse(error: HttpErrorResponse) {
        const message = error.error?.message ?? error.message;
        console.error(message);
        return throwError(() => message);
    }

    validarPermisos(): boolean {
        const permisos = this.registroService.permisos;
        const cantidadPermisos = Array.isArray(permisos) ? permisos.length : 0;
        const userEmail = this.obtenerEmailUsuario();
        if (!this.esUsuarioInstitucional(userEmail)) {
            return true;
        }

        return cantidadPermisos > 0 ? true : false;
    }

    obtenerEmailUsuario(): string {
        const representante = this.registroService
            .representanteAgenteVigilado as {
            email?: string;
        } | null;
        if (representante?.email) {
            return representante.email.trim().toLowerCase();
        }

        const raw = localStorage.getItem('representanteAgenteVigilado');
        if (raw) {
            try {
                const representante = JSON.parse(raw) as { email?: string };
                if (representante?.email) {
                    return representante.email.trim().toLowerCase();
                }
            } catch {
                // JSON inválido
            }
        }

        const email =
            this.keycloakService.getKeycloakInstance().tokenParsed?.['email'];
        return typeof email === 'string' ? email.trim().toLowerCase() : '';
    }

    private esUsuarioInstitucional(email: string | null): boolean {
        if (!email) {
            return false;
        }

        return this.dominiosInstitucionales.some((dominio) =>
            email.endsWith(dominio.toLowerCase())
        );
    }

    tienePermisosInstitucionales(): boolean {
        if (!this.esUsuarioInstitucional(this.obtenerEmailUsuario())) {
            return false;
        }

        const permisos = this.registroService.permisos;
        return Array.isArray(permisos) && permisos.length > 0;
    }

    tieneAlgunoDeLosPermisos(codigos: string[]): boolean {
        if (!codigos.length) {
            return false;
        }

        const permisos = this.registroService.permisos;
        if (!Array.isArray(permisos)) {
            return false;
        }

        const codigosPermitidos = new Set(
            codigos.map((codigo) => codigo.trim()).filter(Boolean)
        );

        return permisos.some((permiso: unknown) => {
            if (typeof permiso === 'string') {
                return codigosPermitidos.has(permiso);
            }

            if (permiso && typeof permiso === 'object') {
                const codigo = (permiso as { codigo?: unknown }).codigo;
                return (
                    typeof codigo === 'string' && codigosPermitidos.has(codigo)
                );
            }

            return false;
        });
    }
}
